import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import toast from "react-hot-toast";
import { RefreshCcw } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import type { RootState, AppDispatch } from "../store";
import { fetchClients } from "../store/clientsSlice";
import type { Client } from "../api/clients";

import {
    type BillingTransaction,
    listBillingTransactionsByClient,
} from "../api/billingTransactionsApi";

function clamp(n: number, min: number, max: number) {
    return Math.min(Math.max(n, min), max);
}


// Example base bucket path:
// https://storage.googleapis.com/<YOUR_BUCKET_NAME>
// or if you use CDN: https://cdn.yourdomain.com/billing
const BUCKET_BASE_URL = process.env.REACT_APP_GCS_BUCKET_BILLING_DOCS;

function buildBucketFileUrl(objectKey?: string) {
    if (!BUCKET_BASE_URL || !objectKey) return "";
    const base = String(BUCKET_BASE_URL).replace(/\/+$/, "");
    const key = String(objectKey).replace(/^\/+/, "");
    // keep slashes but encode spaces, etc.
    return `${base}/${encodeURI(key)}`;
}

function renderId(value: any) {
    if (!value) return "—";
    if (typeof value === "string") return value;
    if (typeof value === "object") return value._id || value.name || "—";
    return String(value);
}

export default function BillingTransactionsPage() {
    const dispatch = useDispatch<AppDispatch>();
    const { items: clients, status: clientsStatus } = useSelector((s: RootState) => s.clients);

    // selection
    const [selectedClientId, setSelectedClientId] = useState<string>("");

    // pagination (backend supports limit/offset)
    // ✅ fixed pagination
    const LIMIT = 10;
    const [page, setPage] = useState<number>(1); // 1-based page

    // data
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<BillingTransaction[]>([]);
    const [total, setTotal] = useState<number>(0);

    // load clients for dropdown
    useEffect(() => {
        // fetch without filters, same pattern as ClientsPage
        dispatch(fetchClients({}));
    }, [dispatch]);

    const selectedClient = useMemo(() => {
        return (clients || []).find((c: Client) => c._id === selectedClientId) ?? null;
    }, [clients, selectedClientId]);

    const canQuery = useMemo(() => !!selectedClientId, [selectedClientId]);

    const offset = (page - 1) * LIMIT;

    const showingFrom = rows.length ? offset + 1 : 0;
    const showingTo = rows.length ? offset + rows.length : 0;

    const totalPages = Math.max(1, Math.ceil(total / LIMIT));


    const fetchList = async () => {
        if (!selectedClientId) {
            setRows([]);
            setTotal(0);
            return;
        }

        const lim = 10;
        const off = Math.max((page - 1) * lim, 0);

        try {
            setLoading(true);
            const res = await listBillingTransactionsByClient({
                clientId: selectedClientId,
                limit: lim,
                offset: off,
            });

            setRows(Array.isArray(res.data) ? res.data : []);
            setTotal(typeof res.total === "number" ? res.total : 0);
        } catch (e: any) {
            toast.error(e?.message || "Failed to load billing transactions");
            setRows([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    // refetch whenever selection/pagination changes
    useEffect(() => {
        fetchList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClientId, page]);

    const onClientChange = (clientId: string) => {
        setSelectedClientId(clientId);
        setPage(1); // reset pagination on change
    };

    const nextDisabled = loading || page >= totalPages || !canQuery;
    const prevDisabled = loading || page <= 1 || !canQuery;


    return (
        <div className="p-6">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-xl font-semibold">Billing Transactions</h1>
                    <p className="text-sm text-gray-500">
                        Listing by client (limit/offset pagination).
                    </p>
                </div>

                <button
                    type="button"
                    onClick={fetchList}
                    className="inline-flex items-center gap-2 rounded-xl border px-4 py-2 hover:bg-gray-50 disabled:opacity-60"
                    disabled={loading || !canQuery}
                >
                    <RefreshCcw size={16} />
                    Refresh
                </button>
            </div>

            {/* Filters */}
            <div className="mb-4 grid grid-cols-12 gap-3 rounded-2xl border bg-white p-4 shadow-sm">
                <div className="col-span-12 sm:col-span-8 lg:col-span-6">
                    <label className="mb-1 block text-xs font-medium text-gray-600">
                        Client
                    </label>

                    <select
                        className="w-full rounded-xl border px-3 py-2 text-sm"
                        value={selectedClientId}
                        onChange={(e) => onClientChange(e.target.value)}
                        disabled={clientsStatus === "loading"}
                    >
                        <option value="">
                            {clientsStatus === "loading" ? "Loading clients..." : "Select client"}
                        </option>

                        {(clients || []).map((c: any) => (
                            <option key={c._id} value={c._id}>
                                {(c?.name || c?.email || c._id) as string}
                            </option>
                        ))}
                    </select>
                </div>
            </div>


            {/* Table */}
            <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-600">
                            <tr>
                                <th className="px-4 py-3">Invoice No</th>
                                <th className="px-4 py-3">Amount</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Paid At</th>
                                <th className="px-4 py-3">Period</th>
                                <th className="px-4 py-3">Subscription</th>
                                <th className="px-4 py-3">File</th>
                                <th className="px-4 py-3">Created</th>
                            </tr>
                        </thead>

                        <tbody className="divide-y">
                            {!loading && !rows.length ? (
                                <tr>
                                    <td colSpan={9} className="px-4 py-10 text-center text-gray-500">
                                        {canQuery ? "No transactions found." : "Select a client to view transactions."}
                                    </td>
                                </tr>
                            ) : null}

                            {rows.map((tx) => {
                                const amountText = `${(tx.amount ?? 0).toFixed(2)} ${(tx.currency ?? "USD").toUpperCase()}`;
                                const paidAt = tx.paidAt ? moment(tx.paidAt).format("MMM DD, YYYY") : "—";
                                const createdAt = tx.createdAt ? moment(tx.createdAt).format("MMM DD, YYYY") : "—";
                                const period =
                                    tx.periodStart && tx.periodEnd
                                        ? `${moment(tx.periodStart).format("MMM DD, YYYY")} → ${moment(tx.periodEnd).format("MMM DD, YYYY")}`
                                        : "—";

                                return (
                                    <tr key={tx._id} className="hover:bg-gray-50/40">
                                        <td className="px-4 py-3">{tx.invoiceNo || "—"}</td>
                                        <td className="px-4 py-3">{amountText}</td>
                                        <td className="px-4 py-3">
                                            <span className="inline-flex rounded-full border px-2 py-1 text-xs">
                                                {tx.paymentStatus || "—"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">{paidAt}</td>
                                        <td className="px-4 py-3">{period}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-col gap-1">
                                                {(() => {
                                                    // clientId may be populated object OR string
                                                    const client: any = tx.clientId;

                                                    const subscriptionModel = client?.subscriptionModel;
                                                    const isPlan = subscriptionModel === "PLAN";

                                                    const planKey =
                                                        client?.currentPlan?.planKey ??
                                                        client?.subscription?.currentPlan?.planKey ??
                                                        null;

                                                    return (
                                                        <>
                                                            {/* Subscription model */}
                                                            <span className="inline-flex w-fit rounded-full border px-2 py-0.5 text-xs">
                                                                {subscriptionModel || "—"}
                                                            </span>

                                                            {/* Plan name (only for PLAN model) */}
                                                            {isPlan ? (
                                                                <span className="text-xs text-gray-500">
                                                                    Plan: <span className="font-medium">{planKey || "—"}</span>
                                                                </span>
                                                            ) : null}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </td>

                                        <td className="px-4 py-3">
                                            <div className="flex flex-col gap-1">
                                                {buildBucketFileUrl(tx.fileUrl) ? (
                                                    <a
                                                        href={buildBucketFileUrl(tx.fileUrl)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="w-fit text-xs font-medium text-blue-700 hover:underline"
                                                        title="Download invoice file"
                                                    >
                                                        Download
                                                    </a>
                                                ) : (
                                                    <span className="text-xs text-gray-400">Bucket base not configured</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">{createdAt}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {loading ? (
                    <div className="px-4 py-6 text-sm text-gray-600">Loading transactions…</div>
                ) : null}
            </div>

            {/* Summary + pagination */}
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-gray-600">
                    {loading ? (
                        "Loading..."
                    ) : (
                        <>
                            Total: <span className="font-medium text-gray-900">{total}</span>
                            {canQuery ? (
                                <>
                                    {" "}
                                    • Showing{" "}
                                    <span className="font-medium text-gray-900">
                                        {showingFrom}-{showingTo}
                                    </span>
                                </>
                            ) : null}
                        </>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        className="rounded-xl border px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-60"
                        disabled={prevDisabled}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}

                    >
                        Prev
                    </button>
                    <button
                        type="button"
                        className="rounded-xl border px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-60"
                        disabled={nextDisabled}
                        onClick={() => setPage((p) => p + 1)}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
}
